import type { BeadColor, PixelData, ColorMapping } from '@pindd/types'

// Weighted color distance based on human perception
function colorDistance(hex1: string, hex2: string): number {
  const r1 = parseInt(hex1.slice(1, 3), 16)
  const g1 = parseInt(hex1.slice(3, 5), 16)
  const b1 = parseInt(hex1.slice(5, 7), 16)
  const r2 = parseInt(hex2.slice(1, 3), 16)
  const g2 = parseInt(hex2.slice(3, 5), 16)
  const b2 = parseInt(hex2.slice(5, 7), 16)

  // Weighted Euclidean distance (redmean approximation)
  const rmean = (r1 + r2) / 2
  const dr = r1 - r2
  const dg = g1 - g2
  const db = b1 - b2

  return Math.sqrt(
    (2 + rmean / 256) * dr * dr +
    4 * dg * dg +
    (2 + (255 - rmean) / 256) * db * db
  )
}

// Normalize hex color
function normalizeHex(hex: string): string {
  const normalized = hex.toLowerCase().trim()
  if (!normalized.startsWith('#')) return '#' + normalized
  return normalized
}

// Quantize a single color by reducing precision
// This groups similar colors together (e.g., round to nearest 32)
function quantizeColor(hex: string, precision: number = 32): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  // Round each channel to nearest precision level
  const qr = Math.round(r / precision) * precision
  const qg = Math.round(g / precision) * precision
  const qb = Math.round(b / precision) * precision

  // Clamp to valid range
  const clamp = (v: number) => Math.min(255, Math.max(0, v))

  return '#' +
    clamp(qr).toString(16).padStart(2, '0') +
    clamp(qg).toString(16).padStart(2, '0') +
    clamp(qb).toString(16).padStart(2, '0')
}

// Pre-process pixel data to group similar colors
export function quantizePixelData(pixelData: PixelData, precision: number = 32): string[][] {
  return pixelData.pixels.map(row =>
    row.map(hex => quantizeColor(normalizeHex(hex), precision))
  )
}

export function findClosestColor(
  hex: string,
  colorLibrary: BeadColor[]
): BeadColor {
  const normalizedHex = normalizeHex(hex)
  let closest = colorLibrary[0]
  let minDistance = Infinity

  for (const color of colorLibrary) {
    const normalizedColorHex = normalizeHex(color.hex)
    const distance = colorDistance(normalizedHex, normalizedColorHex)
    if (distance < minDistance) {
      minDistance = distance
      closest = color
    }
  }

  return closest
}

export function generateColorMapping(
  pixelData: PixelData,
  colorLibrary: BeadColor[],
  precision: number = 32
): ColorMapping[] {
  // Step 1: Quantize pixel data to group similar colors
  const quantizedPixels = quantizePixelData(pixelData, precision)

  // Step 2: Match each quantized pixel to the closest library color
  const matchedColorCounts = new Map<string, number>()

  for (const row of quantizedPixels) {
    for (const hex of row) {
      const matched = findClosestColor(hex, colorLibrary)
      matchedColorCounts.set(matched.id, (matchedColorCounts.get(matched.id) || 0) + 1)
    }
  }

  // Build mapping from matched color IDs
  const mapping: ColorMapping[] = []

  for (const [colorId, count] of matchedColorCounts) {
    const matchedColor = colorLibrary.find((c) => c.id === colorId)
    if (matchedColor) {
      mapping.push({ originalHex: matchedColor.hex, matchedColor, count })
    }
  }

  return mapping.sort((a, b) => b.count - a.count)
}

// Export matched pixel data for rendering
export function generateMatchedPixelData(
  pixelData: PixelData,
  colorLibrary: BeadColor[],
  precision: number = 32
): string[][] {
  // Step 1: Quantize to group similar colors
  const quantizedPixels = quantizePixelData(pixelData, precision)

  // Step 2: Match each pixel to the closest library color
  return quantizedPixels.map(row =>
    row.map(hex => {
      const matched = findClosestColor(hex, colorLibrary)
      return matched.hex
    })
  )
}
